// src/WalletConnectContext.jsx

import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { NearConnector } from "@hot-labs/near-connect";
import useTelegram from "./hooks/useTelegram";
import { apiUrl } from "./config";
import { fetchUserConnectStatus } from "./api/userConnect";
import {
  CONNECT_CLOUD_KEY,
  CONNECT_COOKIE_NAME,
  REFERRAL_COOKIE_NAME,
  REFERRAL_SET_AT_STORAGE_KEY,
  ROOT_COOKIE_DOMAIN,
} from "./projectMeta";
import { getTelegramCloudStorage } from "./utils/telegramWebApp";
import { createWalletProof } from "./utils/walletProof";
import { createCapabilityGuardedConnector } from "./utils/nftTransactionGuard";

/**
 * Єдиний інстанс HOT Connector на додаток
 */
let globalConnectorPromise = null;
const NEAR_CONNECT_MANIFEST_URLS = [
  "https://raw.githubusercontent.com/hot-dao/near-selector/refs/heads/main/repository/manifest.json",
  "https://cdn.jsdelivr.net/gh/azbang/hot-connector/repository/manifest.json",
];
const LOCAL_HOT_WALLET_EXECUTOR_PATH = "wallet-executors/hotwallet.js";

function getLocalHotWalletExecutorUrl() {
  const baseUrl = import.meta.env.BASE_URL || "/";
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const relativePath = `${normalizedBase}${LOCAL_HOT_WALLET_EXECUTOR_PATH}`;

  if (typeof window === "undefined") {
    return relativePath;
  }

  return new URL(relativePath, window.location.origin).toString();
}

async function loadNearConnectorManifest() {
  const hotWalletExecutorUrl = getLocalHotWalletExecutorUrl();

  for (const manifestUrl of NEAR_CONNECT_MANIFEST_URLS) {
    const response = await fetch(manifestUrl).catch(() => null);
    if (!response?.ok) continue;

    const manifest = await response.json().catch(() => null);
    if (!manifest || !Array.isArray(manifest.wallets)) continue;

    return {
      ...manifest,
      wallets: manifest.wallets.map((wallet) =>
        wallet?.id === "hot-wallet"
          ? {
              ...wallet,
              executor: hotWalletExecutorUrl,
            }
          : wallet
      ),
    };
  }

  return null;
}

async function createConnector() {
  const manifest = await loadNearConnectorManifest();

  return createCapabilityGuardedConnector(
    new NearConnector({
      network: "mainnet",
      ...(manifest ? { manifest } : {}),
    })
  );
}

function getConnector() {
  if (!globalConnectorPromise) {
    globalConnectorPromise = createConnector().catch((error) => {
      globalConnectorPromise = null;
      throw error;
    });
  }
  return globalConnectorPromise;
}

const REF_CODE_RE = /^[a-hjkmnpqrstuvwxyz23456789]{10}$/i;
const CONNECT_COOKIE = CONNECT_COOKIE_NAME;
const CONNECT_COOKIE_MAX_AGE = 2592000; // 30 days
const CONNECT_SESSION_PREFIX = `${CONNECT_COOKIE}_`;

function normalizeRefCode(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  if (!s) return null;

  if (s.toLowerCase().startsWith("ref_")) {
    s = s.slice(4).trim();
  }

  if (s.includes("://")) {
    try {
      const u = new URL(s);
      const parts = (u.pathname || "").split("/").filter(Boolean);
      if (parts.length >= 2 && parts[0] === "r") {
        s = parts[1].trim();
      } else if (u.searchParams.get("ref")) {
        s = u.searchParams.get("ref").trim();
      }
    } catch {
      return null;
    }
  }

  return REF_CODE_RE.test(s) ? s : null;
}

function storeRefCode(raw) {
  const code = normalizeRefCode(raw);
  if (!code) return;
  try {
    localStorage.setItem(REFERRAL_COOKIE_NAME, code);
    localStorage.setItem(REFERRAL_SET_AT_STORAGE_KEY, String(Date.now()));
  } catch {
    /* noop */
  }
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${REFERRAL_COOKIE_NAME}=${code}; Max-Age=2592000; Path=/; Domain=${ROOT_COOKIE_DOMAIN}; SameSite=Lax${secure}`;
  } catch {
    /* noop */
  }
}

function readCookie(name) {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  } catch {
    /* noop */
  }
  return null;
}

function setCookie(name, value, maxAgeSeconds = CONNECT_COOKIE_MAX_AGE) {
  if (!name) return;
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* noop */
  }
}

function clearCookie(name) {
  if (!name) return;
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* noop */
  }
}

function getStoredRefCode() {
  try {
    const existing = localStorage.getItem(REFERRAL_COOKIE_NAME);
    if (existing) return existing;
  } catch {
    /* noop */
  }
  return readCookie(REFERRAL_COOKIE_NAME);
}

function makeConnectSessionKey(accountId) {
  if (!accountId) return null;
  return `${CONNECT_SESSION_PREFIX}${accountId}`;
}


function setLocalConnectSession(key) {
  if (!key) return;
  try {
    sessionStorage.setItem(key, "true");
  } catch {
    /* noop */
  }
  try {
    localStorage.setItem(key, "true");
  } catch {
    /* noop */
  }
}

function clearLocalConnectSession(key) {
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* noop */
  }
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}


function cloudSet(tgWebApp, key, value) {
  return new Promise((resolve) => {
    const storage = getTelegramCloudStorage(tgWebApp);
    if (!storage || typeof storage.setItem !== "function") {
      resolve();
      return;
    }
    try {
      storage.setItem(key, value, () => resolve());
    } catch {
      resolve();
    }
  });
}

function cloudRemove(tgWebApp, key) {
  return new Promise((resolve) => {
    const storage = getTelegramCloudStorage(tgWebApp);
    if (!storage) {
      resolve();
      return;
    }
    try {
      if (typeof storage.removeItem === "function") {
        storage.removeItem(key, () => resolve());
      } else if (typeof storage.setItem === "function") {
        storage.setItem(key, "", () => resolve());
      } else {
        resolve();
      }
    } catch {
      resolve();
    }
  });
}


async function setConnectSession(accountId, tgWebApp) {
  const key = makeConnectSessionKey(accountId);
  setLocalConnectSession(key);
  if (accountId) setCookie(CONNECT_COOKIE, accountId);
  // Telegram CloudStorage is auxiliary state; it must never block the UI
  // recovery after a wallet hand-off/signature.
  void cloudSet(tgWebApp, CONNECT_CLOUD_KEY, accountId || "");
}

async function clearConnectSession(accountId, tgWebApp) {
  const key = makeConnectSessionKey(accountId);
  clearLocalConnectSession(key);
  clearCookie(CONNECT_COOKIE);
  await cloudRemove(tgWebApp, CONNECT_CLOUD_KEY);
}

/**
 * Контекст гаманця:
 * - selector: інстанс HOT Connector
 * - connector: те саме, просто більш чесна назва
 * - accountId: активний NEAR-акаунт (або null)
 * - isLoading: триває ініціалізація
 * - hasError: сталася помилка ініціалізації
 * - connect(): відкрити HOT Connector і залогінити юзера
 * - disconnect(): signOut активного гаманця
 */
const WalletContext = createContext({
  selector: null,
  connector: null,
  accountId: null,
  isLoading: true,
  hasError: false,
  connect: async () => {},
  disconnect: async () => {},
  ensureUserConnected: async () => false,
});

export function WalletConnectProvider({ children }) {
  const { tgWebApp, isTelegram } = useTelegram();
  const [selector, setSelector] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // зберігаємо функцію відписки від подій конектора
  const unsubscribeRef = useRef(null);

  // one-session флаг реєстрації користувача через /api_app/api/users/connect
  const didConnectUserRef = useRef(false);
  const connectUserPromiseRef = useRef(null);

  useEffect(() => {
    didConnectUserRef.current = false;
    connectUserPromiseRef.current = null;
  }, [accountId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refParam = params.get("ref");
    if (refParam) {
      storeRefCode(refParam);
    }
    const startParam = tgWebApp?.initDataUnsafe?.start_param;
    if (startParam) {
      storeRefCode(startParam);
    }
    if (!refParam && !startParam) {
      const existing = getStoredRefCode();
      if (existing) storeRefCode(existing);
    }
  }, [tgWebApp, accountId]);

  useEffect(() => {
    try {
      tgWebApp?.ready?.();
    } catch {
      /* noop */
    }
  }, [tgWebApp]);

  // допоміжна функція — синхронізувати accountId з гаманця
  const syncActiveAccount = async (connector) => {
    try {
      if (typeof connector?.getConnectedWallet === "function") {
        const { accounts } = await connector.getConnectedWallet();
        const activeId = accounts?.[0]?.accountId || null;
        setAccountId(activeId);
        return activeId;
      }

      const wallet = await connector.wallet();
      const accounts = (await wallet.getAccounts?.()) || [];
      const activeId = accounts[0]?.accountId || null;
      setAccountId(activeId);
      return activeId;
    } catch (e) {
      console.warn("[wallet] cannot sync active account:", e);
      setAccountId(null);
      return null;
    }
  };

  const ensureUserConnected = useCallback(async ({ force = false } = {}) => {
    if (!selector || !accountId) return false;
    if (window.Telegram?.WebApp && !tgWebApp) return false;
    if (isTelegram && !tgWebApp) return false;
    if (didConnectUserRef.current && !force) return true;
    if (connectUserPromiseRef.current) {
      const inFlight = connectUserPromiseRef.current;
      if (!force) return inFlight;
      await inFlight.catch(() => false);
      connectUserPromiseRef.current = null;
    }
    if (force) didConnectUserRef.current = false;

    const promise = (async () => {
      const canonicalStatus = await fetchUserConnectStatus({
        accountId,
        initData: tgWebApp?.initData || "",
      }).catch(() => null);

      const pendingRefCode = isTelegram ? "" : (getStoredRefCode() || "");

      if (canonicalStatus?.is_connected && !pendingRefCode && !canonicalStatus?.pending_referral) {
        await setConnectSession(accountId, tgWebApp);
        didConnectUserRef.current = true;
        return true;
      }

      const wallet_proof = await createWalletProof({ selector, accountId });

      const payload = {
        wallet_proof,
        ref_code: pendingRefCode,
        init_data: tgWebApp?.initData || "",
      };

      const postConnect = () =>
        fetch(apiUrl("users/connect"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }).catch(() => null);

      const firstRes = await postConnect();
      let connectOk = firstRes?.ok || firstRes?.status === 409;
      if (!connectOk) {
        const retryRes = await postConnect();
        connectOk = retryRes?.ok || retryRes?.status === 409;
      }

      if (!connectOk) {
        throw new Error("users/connect failed");
      }

      await setConnectSession(accountId, tgWebApp);
      didConnectUserRef.current = true;

      const referralFlowCompleted = Boolean(pendingRefCode || canonicalStatus?.pending_referral);
      if (isTelegram && referralFlowCompleted) {
        // HOT/Telegram wallet hand-off can leave the current WebView document
        // in an empty DOM after the signature callback. The referral bind is
        // already committed by users/connect, so a clean /home reload is safe
        // and prevents a blank Mini App after successful signing.
        window.setTimeout(() => {
          try {
            window.location.replace("/home");
          } catch {
            /* noop */
          }
        }, 50);
      }

      localStorage.removeItem(REFERRAL_COOKIE_NAME);
      localStorage.removeItem(REFERRAL_SET_AT_STORAGE_KEY);
      try {
        document.cookie = `${REFERRAL_COOKIE_NAME}=; Max-Age=0; Path=/; Domain=${ROOT_COOKIE_DOMAIN}; SameSite=Lax`;
      } catch {
        /* noop */
      }

      return true;
    })()
      .catch(() => false)
      .finally(() => {
        connectUserPromiseRef.current = null;
      });

    connectUserPromiseRef.current = promise;
    return promise;
  }, [selector, accountId, tgWebApp, isTelegram]);

  useEffect(() => {
    if (!selector || !accountId) return;

    if (!isTelegram) {
      if (getStoredRefCode()) {
        void ensureUserConnected({ force: true });
      }
      return;
    }

    if (!tgWebApp?.initData) return;
    let cancelled = false;
    void fetchUserConnectStatus({
      accountId,
      initData: tgWebApp.initData,
    })
      .then((status) => {
        // A referral arriving from Telegram is itself an authenticated reason
        // to complete the wallet↔Telegram connection. This must also run when
        // the wallet is not connected to Telegram yet; requiring is_connected
        // here used to leave new referral users stuck on the manual
        // "Прив'язати Telegram" prompt.
        if (!cancelled && status?.pending_referral) {
          void ensureUserConnected({ force: true });
        }
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [selector, accountId, isTelegram, tgWebApp, ensureUserConnected]);

  // Ініціалізація HOT Connector
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setIsLoading(true);
      setHasError(false);

      try {
        const connector = await getConnector();
        if (cancelled) return;

        setSelector(connector);

        // 1) Спроба відновити активний акаунт з наявної сесії
        await syncActiveAccount(connector);

        // 2) Підписка на події signIn/signOut конектора
        try {
          unsubscribeRef.current?.();
        } catch {
          /* noop */
        }

        const handleSignIn = async (event) => {
          // event?.accounts[0].accountId або повторна синхронізація
          const fromEvent = event?.accounts?.[0]?.accountId;
          if (fromEvent) {
            setAccountId(fromEvent);
          } else {
            await syncActiveAccount(connector);
          }
        };

        const handleSignOut = () => {
          setAccountId(null);
        };

        if (typeof connector.on === "function") {
          connector.on("wallet:signIn", handleSignIn);
          connector.on("wallet:signOut", handleSignOut);

          unsubscribeRef.current = () => {
            if (typeof connector.off === "function") {
              connector.off("wallet:signIn", handleSignIn);
              connector.off("wallet:signOut", handleSignOut);
            }
          };
        } else {
          // якщо раптом немає .on/.off — просто не підписуємося
          unsubscribeRef.current = null;
        }

        setHasError(false);
      } catch (e) {
        console.warn("[wallet] init error:", e);
        if (!cancelled) {
          setHasError(true);
          setSelector(null);
          setAccountId(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
      try {
        unsubscribeRef.current?.();
      } catch {
        /* noop */
      }
      unsubscribeRef.current = null;
    };
  }, [tgWebApp]);

  // connect через HOT Connector
  const connect = async () => {
    if (!selector) return false;

    try {
      // відкриває модалку з гаманцями, юзер логіниться
      await selector.connect();

      // accountId оновиться через подію "wallet:signIn"
      // але можна на всяк випадок попробувати синхронізувати
      await syncActiveAccount(selector);

      return true;
    } catch (e) {
      console.error("[wallet] connect error:", e);
      throw e; // щоб верхній рівень (WebAppLayout) міг показати тост
    }
  };

  // disconnect
  const disconnect = async () => {
    if (!selector) return;
    try {
      if (typeof selector.disconnect === "function") {
        await selector.disconnect();
      } else {
        const wallet = await selector.wallet();
        await wallet.signOut?.();
      }
      await clearConnectSession(accountId, tgWebApp);
      didConnectUserRef.current = false;
      connectUserPromiseRef.current = null;
      setAccountId(null);
    } catch (e) {
      console.error("[wallet] disconnect error:", e);
      throw e;
    }
  };

  // Сумісність з window.showWalletSelector() для старого коду
  useEffect(() => {
    const fn = connect;
    window.showWalletSelector = fn;
    return () => {
      if (window.showWalletSelector === fn) {
        try {
          delete window.showWalletSelector;
        } catch {
          window.showWalletSelector = undefined;
        }
      }
    };
  }, [selector]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <WalletContext.Provider
      value={{
        selector,          // тепер це HOT NearConnector
        connector: selector,
        accountId,
        isLoading,
        hasError,
        connect,
        disconnect,
        ensureUserConnected,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWalletConnect() {
  return useContext(WalletContext);
}

export default WalletConnectProvider;
